import { inferRouteObject } from 'react-router-typed-object';

import { isDebug } from '@/js/libs/is-debug';
import { Navigate } from '@/js/libs/router';
import { PrivateRoute, Roles, or } from '@/js/libs/use-auth';

import { INVOICING_PAGE_ROLES } from 'src/access/invoicingAccess.ts';
import SelfBooking from 'src/scenes/SelfBooking/index.ts';
import { campaignCreateFormRouterSearchParamsSchema } from 'src/scenes/SelfBooking/pages/Campaigns/containers/CampaignCreateForm/types.ts';
import { campaignsListRouterSearchParamsSchema } from 'src/scenes/SelfBooking/pages/Campaigns/containers/CampaignsList/types.ts';
import { positionFormSearchParamsSchema } from 'src/scenes/SelfBooking/pages/Campaigns/containers/PositionCreateForm/types.ts';
import Status from 'src/scenes/Status/index.ts';

const index = true;

export const selfBookingRouteObject = inferRouteObject({
  path: 'self-booking',
  element: <PrivateRoute roles={Roles.YALEO_SELF_BOOKING} noAccessComponent={Status.NotFound} />,
  children: [
    {
      Component: SelfBooking.Helmet,
      children: [
        { index, element: <Navigate to='./campaigns' replace /> },
        {
          path: 'dashboard',
          // Held back from users until sign-off: reachable only behind the debug flag, see js/rules/DebugMode.md.
          children: [{ index, Component: isDebug() ? SelfBooking.Dashboard : Status.NotFound }],
        },
        {
          path: 'campaigns',
          searchParams: campaignsListRouterSearchParamsSchema.parse,
          children: [
            { index, Component: SelfBooking.Campaigns.CampaignsList },
            {
              path: 'create',
              Component: SelfBooking.Campaigns.CampaignCreateForm,
              searchParams: campaignCreateFormRouterSearchParamsSchema.parse,
            },
            {
              path: ':campaignId',
              children: [
                { index, Component: SelfBooking.Campaigns.CampaignOverview },
                {
                  path: 'position',
                  children: [
                    { index, element: <Navigate to='./create' replace /> },
                    {
                      path: 'create',
                      Component: SelfBooking.Campaigns.PositionCreateForm,
                      searchParams: positionFormSearchParamsSchema.parse,
                    },
                    {
                      path: 'edit',
                      children: [
                        { index, element: <Navigate to='../create' replace /> },
                        {
                          path: ':positionId',
                          Component: SelfBooking.Campaigns.PositionEditForm,
                          searchParams: positionFormSearchParamsSchema.parse,
                        },
                      ],
                    },
                    {
                      path: 'history',
                      children: [
                        { index, element: <Navigate to='../' replace /> },
                        { path: ':positionId', Component: SelfBooking.Campaigns.PositionHistory },
                      ],
                    },
                  ],
                },
                {
                  path: 'summary',
                  Component: SelfBooking.Campaigns.CampaignSummary,
                },
                {
                  path: 'campaign-history',
                  Component: SelfBooking.Campaigns.CampaignHistory,
                },
                {
                  path: 'order-preview',
                  Component: SelfBooking.Campaigns.OrderPreview,
                },
              ],
            },
          ],
        },
        {
          path: 'advertisers',
          children: [
            { index, Component: SelfBooking.Advertisers.AdvertisersList },
            {
              path: ':advertiserId',
              children: [
                { index, Component: SelfBooking.Advertisers.AdvertiserOverview },
                {
                  path: 'conversion-tracker',
                  children: [
                    { index, element: <Navigate to='./create' replace /> },
                    { path: 'create', Component: SelfBooking.Advertisers.ConversionTrackerForm },
                    {
                      path: 'edit',
                      children: [
                        { index, element: <Navigate to='../create' replace /> },
                        { path: ':conversionTrackerId', Component: SelfBooking.Advertisers.ConversionTrackerForm },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: 'creatives',
          children: [
            { index, Component: SelfBooking.Creatives.CreativesList },
            { path: 'create', Component: SelfBooking.Creatives.CreativeForm },
            {
              path: 'edit',
              children: [
                { index, element: <Navigate to='../' replace /> },
                { path: ':creativeId', Component: SelfBooking.Creatives.CreativeForm },
              ],
            },
          ],
        },
        {
          path: 'positions',
          children: [{ index, Component: SelfBooking.Positions }],
        },
        {
          path: 'creatives-approval',
          element: <PrivateRoute roles={or(Roles.OPS_CHECKS, Roles.ADMIN)} noAccessComponent={Status.NotFound} />,
          children: [{ index, Component: SelfBooking.CreativesApproval.CreativesApprovalList }],
        },
        {
          path: 'screenshot-management',
          element: <PrivateRoute roles={or(Roles.OPS_SCREENSHOTS, Roles.ADMIN)} noAccessComponent={Status.NotFound} />,
          children: [{ index, Component: SelfBooking.ScreenshotManagement.ScreenshotManagementList }],
        },
        {
          path: 'invoicing',
          element: <PrivateRoute roles={INVOICING_PAGE_ROLES} noAccessComponent={Status.NotFound} />,
          children: [{ index, Component: SelfBooking.Invoicing.InvoicingList }],
        },
        {
          path: 'receipts',
          Component: SelfBooking.Receipts.Layout,
          children: [
            { index, element: <Navigate to='./payments' replace /> },
            {
              path: 'payments',
              Component: SelfBooking.Receipts.Payments,
            },
            {
              path: 'payouts',
              element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
              children: [{ index, Component: SelfBooking.Receipts.Payouts }],
            },
          ],
        },
        {
          path: 'payment-methods',
          Component: SelfBooking.PaymentMethods.PaymentMethods,
        },
      ],
    },
  ],
});
